import React, { useEffect } from 'react';
import { SITE_URL, LANGS, DEFAULT_LANG, buildHomeCanonicalPath } from '../utils/seoUrls';

/**
 * Default SEO injected by MainLayout for routes that don't define their own
 * dynamic SEO. React 19 hoists these tags to <head>; we also imperatively set
 * document.title in a useEffect so server-side prerendering layers that
 * serialize the final DOM (rather than processing React's head hoist) still
 * pick up the title.
 *
 * Tone is "warm, local, trustworthy" per LebonLux brand guidelines.
 * Copy is native-written per language — not literal translations.
 */
const DEFAULTS = {
  fr: {
    title: 'Petites annonces Luxembourg — Acheter & vendre | LebonLux',
    description:
      "Achetez et vendez facilement au Luxembourg sur LebonLux : immobilier, voitures d'occasion, électronique, meubles, mode et petites annonces locales gratuites.",
    ogTitle: 'LebonLux — Marketplace local du Luxembourg',
    ogDescription:
      "Achetez et vendez d'occasion au Luxembourg et dans la Grande Région — en toute confiance.",
    keywords:
      'marketplace luxembourg, annonces luxembourg, occasion luxembourg, meubles luxembourg, électronique luxembourg, expat luxembourg, grande région, déménagement luxembourg',
    ogLocale: 'fr_LU',
  },
  en: {
    title: 'Classified ads Luxembourg — Buy & sell | LebonLux',
    description:
      'Buy and sell easily in Luxembourg on LebonLux: real estate, used cars, electronics, furniture, fashion and free local classified ads.',
    ogTitle: 'LebonLux — Local marketplace for Luxembourg',
    ogDescription:
      'Buy and sell second-hand in Luxembourg and the Greater Region — in full confidence.',
    keywords:
      'luxembourg marketplace, second-hand luxembourg, used items luxembourg, expat luxembourg, greater region, moving sale luxembourg, furniture luxembourg',
    ogLocale: 'en_LU',
  },
  de: {
    title: 'Kleinanzeigen Luxemburg — Kaufen & verkaufen | LebonLux',
    description:
      'Einfach kaufen und verkaufen in Luxemburg auf LebonLux: Immobilien, Gebrauchtwagen, Elektronik, Möbel, Mode und kostenlose lokale Kleinanzeigen.',
    ogTitle: 'LebonLux — Lokaler Marktplatz für Luxemburg',
    ogDescription:
      'Kaufen und verkaufen Sie Gebrauchtwaren in Luxemburg und der Großregion — ganz in Vertrauen.',
    keywords:
      'marktplatz luxemburg, gebrauchtwaren luxemburg, kleinanzeigen luxemburg, expat luxemburg, großregion, umzug luxemburg, möbel luxemburg',
    ogLocale: 'de_LU',
  },
  lb: {
    title: "Annoncen Lëtzebuerg — Kafen & verkafen | LebonLux",
    description:
      "Einfach kafen a verkafen zu Lëtzebuerg op LebonLux: Immobilien, Gebrauchtautoen, Elektronik, Miwwel, Moud a gratis lokal Annoncen.",
    ogTitle: 'LebonLux — Lokale Marketplace fir Lëtzebuerg',
    ogDescription:
      "Kaaft a verkaaft Geleeënheetsgéigestänn zu Lëtzebuerg an der Groussregioun — mat all Vertrauen.",
    keywords:
      'marketplace lëtzebuerg, geleeënheetsannoncen lëtzebuerg, gebraucht lëtzebuerg, ëmzug lëtzebuerg, miwwelen lëtzebuerg, expat lëtzebuerg, groussregioun',
    ogLocale: 'lb_LU',
  },
};

const OG_IMAGE = `${SITE_URL}/og-image.png`;

export const DefaultSEO = ({
  lang = 'fr',
  title,
  description,
  ogTitle,
  ogDescription,
  ogImage = OG_IMAGE,
  canonicalPath,
  keywords,
}) => {
  const d = DEFAULTS[lang] || DEFAULTS.fr;
  const finalTitle = title || d.title;
  const finalDescription = description || d.description;
  const finalOgTitle = ogTitle || d.ogTitle || finalTitle;
  const finalOgDescription = ogDescription || d.ogDescription || finalDescription;
  const finalKeywords = keywords || d.keywords;
  // Self-referencing canonical: use the active language's home path. fr → "/",
  // en/de/lb → "/{lang}/". If a caller passes an explicit canonicalPath, use it.
  const finalCanonicalPath = canonicalPath || buildHomeCanonicalPath(lang);
  const finalCanonical = `${SITE_URL}${finalCanonicalPath}`;

  // Imperative document.title — caught by SSR/prerender DOM serialization
  // even when React 19's <title> JSX hoisting isn't applied.
  useEffect(() => {
    document.title = finalTitle;
  }, [finalTitle]);

  // hreflang alternates: emit one link per language pointing at the
  // corresponding /{lang}/ home (or "/" for fr). Only emit when this is the
  // home page (i.e., no explicit canonicalPath was supplied) — for non-home
  // routes the caller should pass canonicalPath and provide its own hreflang.
  const isHome = !canonicalPath;
  const hreflangs = isHome
    ? [
        ...LANGS.map((l) => ({
          lang: l,
          url: `${SITE_URL}${buildHomeCanonicalPath(l)}`,
        })),
        { lang: 'x-default', url: `${SITE_URL}${buildHomeCanonicalPath(DEFAULT_LANG)}` },
      ]
    : [];

  return (
    <>
      <title>{finalTitle}</title>
      <meta name="title" content={finalTitle} />
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <link rel="canonical" href={finalCanonical} />

      {/* hreflang alternates — emitted for home routes now that Phase 2 has
          real /{lang}/ home pages. Non-home routes provide their own. */}
      {hreflangs.map((h) => (
        <link key={`dseo-hl-${h.lang}`} rel="alternate" hrefLang={h.lang} href={h.url} />
      ))}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={d.ogLocale} />
      <meta property="og:site_name" content="LebonLux" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={finalCanonical} />
      <meta name="twitter:title" content={finalOgTitle} />
      <meta name="twitter:description" content={finalOgDescription} />
      <meta name="twitter:image" content={ogImage} />
    </>
  );
};

export default DefaultSEO;
